import { httpClient } from '@/services/api';
import { ENV } from '@/app/config';
import type {
	TGender,
	TPerson,
	TPersonUpdatePayload,
	TConsentUpdatePayload,
	TDadosUsuarioApiUser,
	TDadosUsuarioResponse
} from './person.types';

const DADOS_USUARIO_URL = `${ENV.API_PF_URL}v1/acesso/dados-usuario`;
const ALTERAR_PF_URL = `${ENV.API_PF_URL}v1/anunciante/alterar-pf`;
const UPDATE_CONSENT_PF_URL = `${ENV.API_PF_URL}v1/anunciante/update-consent-pf`;

const mapApiUserToPerson = (u: TDadosUsuarioApiUser): TPerson => ({
	id: u.Id,
	userCode: u.CodigoUsuario,
	name: u.Nome,
	adName: u.NomeAnuncio,
	document: u.CPF,
	email: u.Email,
	zipCode: u.CEP,
	city: u.Cidade,
	state: u.Uf,
	birthDate: u.DataNascimento,
	primaryPhone: { areaCode: u.DDDPrincipal, number: u.TelefonePrincipal },
	secondaryPhone: { areaCode: u.DDDSecundario, number: u.TelefoneSecundario },
	gender: u.Sexo as TGender,
	activeAd: u.AnuncioAtivo,
	approved: u.Aprovado,
	emailConfirmed: u.EmailConfirmado,
	mustConfirmEmail: u.DeveConfirmarEmail,
	status: u.Situacao,
	recentlyMigrated: u.RecemMigrado,
	originChannel: u.CanalOrigem,
	showPhone: u.ShowPhone,
	showPhoneChanged: false,
	wantPartnerPromotions: u.DesejaReceberPromocoesWebMotorsParc,
	wantWebMotorsInfo: u.DesejoReceberInformacoesWebMotors,
	passwordExpired: u.SenhaExpirada,
	incompleteRegistration: u.CadastroIncompleto,
	detectedRestrictiveList: u.DetectadoListaRestritiva
});

const mapUpdatePayloadToApi = (
	p: TPersonUpdatePayload
): Partial<TDadosUsuarioApiUser> => ({
	...(p.name !== undefined && { Nome: p.name }),
	...(p.document !== undefined && { CPF: p.document }),
	...(p.email !== undefined && { Email: p.email }),
	...(p.gender !== undefined && { Sexo: p.gender }),
	...(p.birthDate !== undefined && { DataNascimento: p.birthDate }),
	...(p.zipCode !== undefined && { CEP: p.zipCode }),
	...(p.city !== undefined && { Cidade: p.city }),
	...(p.state !== undefined && { Uf: p.state }),
	...(p.showPhone !== undefined && { ShowPhone: p.showPhone })
});

const fetchRawApiUser = async (): Promise<TDadosUsuarioApiUser> => {
	const { data } = await httpClient.post<TDadosUsuarioResponse>(DADOS_USUARIO_URL, { hash: '' });
	return data.Retorno;
};

const fetchDadosUsuario = async (): Promise<TPerson> =>
	mapApiUserToPerson(await fetchRawApiUser());

const personService = {
	getData: fetchDadosUsuario,

	update: async (payload: TPersonUpdatePayload): Promise<TPerson> => {
		await httpClient.post(ALTERAR_PF_URL, mapUpdatePayloadToApi(payload));
		return fetchDadosUsuario();
	},

	updateConsent: async (payload: TConsentUpdatePayload): Promise<TPerson> => {
		const rawUser = await fetchRawApiUser();
		const consentPayload: TDadosUsuarioApiUser = {
			...rawUser,
			DesejaReceberPromocoesWebMotorsParc: payload.wantPartnerPromotions,
			DesejoReceberInformacoesWebMotors: payload.wantWebMotorsInfo
		};
		await httpClient.post(UPDATE_CONSENT_PF_URL, consentPayload);
		return mapApiUserToPerson(consentPayload);
	}
} as const;

export { personService };
